/**
 * Token Utilities
 *
 * JWT token handling utilities for authentication
 * Client-side token decoding, validation, and storage
 */

import type { JWTPayload, LoginResponse } from '@/stores/auth.types';

/**
 * Decode a JWT token without verification (client-side only)
 * @param token - JWT token string
 * @returns Decoded JWT payload
 * @throws Error if token is invalid
 */
export function decodeJWT(token: string): JWTPayload {
  try {
    // JWT structure: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid token format');
    }

    // Decode the payload (second part)
    const payload = parts[1];
    if (!payload) {
      throw new Error('Invalid token payload');
    }

    // Replace URL-safe characters and add padding if needed
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );

    // Decode base64
    const decoded = atob(padded);

    // Parse JSON
    return JSON.parse(decoded) as JWTPayload;
  } catch (error) {
    throw new Error(
      `Failed to decode JWT: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Check if a token is expired based on its expiry timestamp
 * @param expiryTimestamp - Unix timestamp (seconds)
 * @returns true if expired, false otherwise
 */
export function isTokenExpired(expiryTimestamp: number): boolean {
  const now = Math.floor(Date.now() / 1000); // Current time in seconds
  return now >= expiryTimestamp;
}

/**
 * Check if a token needs refresh (expires in less than 5 minutes)
 * @param expiryTimestamp - Unix timestamp (seconds)
 * @returns true if refresh needed, false otherwise
 */
export function needsRefresh(expiryTimestamp: number): boolean {
  const now = Math.floor(Date.now() / 1000);
  const fiveMinutes = 5 * 60;
  return expiryTimestamp - now < fiveMinutes;
}

/**
 * Store authentication tokens in localStorage
 * @param response - Login response containing tokens
 */
export function storeTokens(response: LoginResponse): void {
  try {
    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('accessTokenExp', response.accessTokenExp.toString());
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem(
      'refreshTokenExp',
      response.refreshTokenExp.toString()
    );

    // Decode and store provider email
    const decoded = decodeJWT(response.accessToken);
    localStorage.setItem('providerEmail', decoded.sub);
    localStorage.setItem('sessionId', decoded.jti);
  } catch (error) {
    console.error('Failed to store tokens:', error);
    throw error;
  }
}

/**
 * Retrieve authentication tokens from localStorage
 * @returns Token data or null if not found
 */
export function getTokens(): {
  accessToken: string;
  accessTokenExp: number;
  refreshToken: string;
  refreshTokenExp: number;
  providerEmail: string;
  sessionId: string;
} | null {
  const accessToken = localStorage.getItem('accessToken');
  const accessTokenExp = localStorage.getItem('accessTokenExp');
  const refreshToken = localStorage.getItem('refreshToken');
  const refreshTokenExp = localStorage.getItem('refreshTokenExp');
  const providerEmail = localStorage.getItem('providerEmail');
  const sessionId = localStorage.getItem('sessionId');

  if (
    !accessToken ||
    !accessTokenExp ||
    !refreshToken ||
    !refreshTokenExp ||
    !providerEmail ||
    !sessionId
  ) {
    return null;
  }

  return {
    accessToken,
    accessTokenExp: parseInt(accessTokenExp, 10),
    refreshToken,
    refreshTokenExp: parseInt(refreshTokenExp, 10),
    providerEmail,
    sessionId,
  };
}

/**
 * Clear all authentication tokens from localStorage
 */
export function clearTokens(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('accessTokenExp');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('refreshTokenExp');
  localStorage.removeItem('providerEmail');
  localStorage.removeItem('sessionId');
  localStorage.removeItem('providerId');
  localStorage.removeItem('providerName');
}

/**
 * Get formatted time until token expiry
 * @param expiryTimestamp - Unix timestamp (seconds)
 * @returns Human-readable time string (e.g., "5 minutes", "2 hours")
 */
export function getTimeUntilExpiry(expiryTimestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const secondsLeft = expiryTimestamp - now;

  if (secondsLeft <= 0) {
    return 'expired';
  }

  const minutes = Math.floor(secondsLeft / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''}`;
  }
  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
  if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
  return `${secondsLeft} second${secondsLeft > 1 ? 's' : ''}`;
}

/**
 * Validate token structure without full verification
 * @param token - JWT token string
 * @returns true if token structure is valid
 */
export function isValidTokenFormat(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }

    // Try to decode the payload
    decodeJWT(token);
    return true;
  } catch {
    return false;
  }
}
